// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
  useColorScheme,
} from 'react-native';
import { createTheme } from '../design-system/theme';
import { MiniProfileActions } from './MiniProfileActions';
import { MiniProfileHeader } from './MiniProfileHeader';
import { profileStrings } from './strings';
import type { UserId } from '../util/ids';
import type {
  MiniProfileActionKey,
  MiniProfileLoader,
  MiniProfileSide,
  MiniProfileSubscriber,
  UserProfile,
} from './types';
import { useMiniProfileAnchor } from './useMiniProfileAnchor';
import { useMiniProfileViewModel } from './useMiniProfileViewModel';

export interface MiniProfileCardProps {
  readonly userId: UserId;
  readonly load: MiniProfileLoader;
  readonly subscribe: MiniProfileSubscriber;
  readonly anchorRef: React.RefObject<unknown>;
  readonly side?: MiniProfileSide;
  readonly offset?: number;
  readonly onAction?: ((action: MiniProfileActionKey, profile: UserProfile) => void) | undefined;
  readonly onDismiss?: (() => void) | undefined;
}

const CARD_WIDTH = 320;

/**
 * Web/desktop mini-profile popover. Anchored to a `RefObject` so it stays
 * pinned across reflows and scroll events. The card has its own focus
 * dismiss flow:
 *   - `Escape` (web/macOS keyboard) → `onDismiss`
 *   - Click outside the card → host wires `onDismiss` against its overlay
 *
 * The card never owns its own scrim; the host renders it inside a
 * portal/overlay of its choice (this keeps `MiniProfileCard` reusable
 * inside both hover-popover and pinned-panel contexts).
 */
export const MiniProfileCard: React.FC<MiniProfileCardProps> = ({
  userId,
  load,
  subscribe,
  anchorRef,
  side = 'bottom',
  offset = 8,
  onAction,
  onDismiss,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const vm = useMiniProfileViewModel({ userId, load, subscribe });
  const anchor = useMiniProfileAnchor({
    anchorRef,
    side,
    offset,
    cardWidth: CARD_WIDTH,
  });
  const cardRef = useRef<unknown>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onDismiss?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDismiss]);

  const handleAction = useCallback(
    (key: MiniProfileActionKey) => {
      if (vm.state.kind === 'loaded') {
        onAction?.(key, vm.state.profile);
      }
    },
    [vm.state, onAction],
  );

  const renderBody = (): React.ReactElement => {
    if (vm.state.kind === 'loading') {
      return (
        <View style={styles.center} accessibilityRole="progressbar">
          <ActivityIndicator size="small" color={theme.colors.accent.indigo} />
          <Text style={[styles.muted, { color: theme.colors.text.secondary }]}>
            {profileStrings.loading}
          </Text>
        </View>
      );
    }
    if (vm.state.kind === 'notFound') {
      return (
        <View style={styles.center}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]}>
            {profileStrings.notFound}
          </Text>
        </View>
      );
    }
    if (vm.state.kind === 'blocked') {
      return (
        <View style={styles.center}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]}>
            {profileStrings.blocked}
          </Text>
        </View>
      );
    }
    if (vm.state.kind === 'error') {
      return (
        <View style={styles.center}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]}>
            {profileStrings.error}
          </Text>
          <Pressable
            onPress={vm.retry}
            accessibilityRole="button"
            accessibilityLabel={profileStrings.retry}
            style={({ pressed }) => [
              styles.retryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text style={styles.retryText}>{profileStrings.retry}</Text>
          </Pressable>
        </View>
      );
    }

    const profile = vm.state.profile;
    return (
      <>
        <MiniProfileHeader profile={profile} />
        <View style={styles.bodyBlock}>
          <Text style={[styles.bio, { color: theme.colors.text.primary }]} numberOfLines={3}>
            {profile.bio ?? profileStrings.bioFallback}
          </Text>
          {profile.mutualContactCount > 0 && (
            <Text style={[styles.mutual, { color: theme.colors.text.secondary }]}>
              {profileStrings.mutualContacts(profile.mutualContactCount)}
            </Text>
          )}
        </View>
        <MiniProfileActions profile={profile} onAction={handleAction} />
      </>
    );
  };

  if (!anchor.rect.visible) {
    return null;
  }

  const positionStyle: ViewStyle =
    Platform.OS === 'web' || Platform.OS === 'macos'
      ? {
          position: 'absolute',
          top: anchor.rect.top,
          left: anchor.rect.left,
          width: CARD_WIDTH,
        }
      : { width: CARD_WIDTH };

  return (
    <View
      ref={cardRef as React.MutableRefObject<View | null>}
      accessibilityRole="menu"
      accessibilityLabel="Mini profile"
      style={[
        styles.card,
        positionStyle,
        {
          backgroundColor: theme.colors.surface.primary,
          borderColor: theme.colors.border.secondary,
          ...theme.shadows.md,
        },
      ]}
    >
      {renderBody()}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: 'hidden',
  } satisfies ViewStyle,
  center: {
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  muted: {
    marginTop: 8,
    fontSize: 12,
  } satisfies TextStyle,
  title: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  } satisfies TextStyle,
  bodyBlock: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  } satisfies ViewStyle,
  bio: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 6,
  } satisfies TextStyle,
  mutual: {
    fontSize: 12,
    fontWeight: '500',
  } satisfies TextStyle,
  retryButton: {
    marginTop: 12,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
  } satisfies ViewStyle,
  retryText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
